"use client";
import { twMerge } from "tailwind-merge";
import type { LinkComponentType } from "../../index";
import { Card } from "../Card";
import { StaticMap } from "../Maps/StaticMap";
import { Skeleton } from "../Skeleton";
import { TextLink } from "../TextLink";

export interface SiteCardProps {
  /** Street address */
  streetAddress: string;

  /** City name (optional) */
  city?: string;

  /** State/province (optional) */
  state?: string;

  /** Postal/ZIP code (optional) */
  postalCode?: string;

  /** Longitude for map display */
  longitude?: number;

  /** Latitude for map display */
  latitude?: number;

  /** Link to site detail page (optional) */
  href?: string;

  /** Custom link component (e.g., Next.js Link) */
  LinkComponent?: LinkComponentType;

  /** Card variant */
  variant?: "outlined" | "elevated" | "filled" | "ghost";

  /** Layout mode
   * @default "stacked"
   * - "stacked": Traditional stacked layout (map on top, content below)
   * - "row": Full-width row layout on desktop (address left, map right), stacked on mobile
   */
  layout?: "stacked" | "row";

  /** Show map (requires longitude/latitude) */
  showMap?: boolean;

  /** Map height */
  mapHeight?: number | string;

  /** Mapbox access token (required for map display) */
  mapboxAccessToken?: string;

  /** Loading state */
  isLoading?: boolean;

  /** Additional classes for the card */
  className?: string;

  /** Additional classes for the card content (for padding control, etc.) */
  contentClassName?: string;

  /** Show expand button on map to open interactive fullscreen view
   * @default false
   */
  showMapExpandToggle?: boolean;

  /** Title for the expanded map dialog
   * @default "Explore map"
   */
  expandedMapTitle?: string;

  /** Address label to show in expanded map view (defaults to formatted address) */
  expandedMapAddressLabel?: string;

  /**
   * Entity identifier for PII masking (e.g., "site:site_abc123")
   * When provided, adds data-pii-* attributes for the Curtain extension
   */
  piiEntity?: string;
}

/**
 * SiteCard
 *
 * A card component for displaying site/location information.
 * Can include an optional static map showing the site's location.
 *
 * @example
 * ```tsx
 * <SiteCard
 *   streetAddress="123 Main St"
 *   city="San Francisco"
 *   state="CA"
 *   postalCode="94102"
 *   longitude={-122.4194}
 *   latitude={37.7749}
 *   showMap
 *   href="/sites/123"
 * />
 * ```
 */
export function SiteCard({
  streetAddress,
  city,
  state,
  postalCode,
  longitude,
  latitude,
  href,
  LinkComponent,
  variant = "outlined",
  layout = "stacked",
  showMap = true,
  mapHeight = 200,
  mapboxAccessToken,
  isLoading = false,
  className,
  contentClassName,
  showMapExpandToggle = false,
  expandedMapTitle = "Explore map",
  expandedMapAddressLabel,
  piiEntity,
}: SiteCardProps) {
  // Build city/state/zip line
  const locationParts: string[] = [];
  if (city) locationParts.push(city);
  if (state && postalCode) {
    locationParts.push(`${state} ${postalCode}`);
  } else if (state) {
    locationParts.push(state);
  } else if (postalCode) {
    locationParts.push(postalCode);
  }
  const locationLine = locationParts.join(", ");

  // Build full address for expanded map label
  const fullAddress = expandedMapAddressLabel || [streetAddress, locationLine].filter(Boolean).join(", ");

  // Determine if we can show the map
  const canShowMap = showMap && longitude !== undefined && latitude !== undefined;

  // Link wrapper component
  const LinkWrapper = ({ children, className: linkClassName }: { children: React.ReactNode; className?: string }) => {
    if (!href) {
      return <span className={linkClassName}>{children}</span>;
    }

    if (LinkComponent) {
      return (
        <LinkComponent href={href} className={linkClassName}>
          {children}
        </LinkComponent>
      );
    }

    return (
      <TextLink href={href} variant="unstyled" className={twMerge("hover:underline", linkClassName)} asButton={false}>
        {children}
      </TextLink>
    );
  };

  if (isLoading) {
    const isRowLayout = layout === "row";

    return (
      <Card
        variant={variant}
        className={twMerge(isRowLayout ? "flex flex-col md:flex-row" : "flex flex-col", className)}
      >
        {canShowMap && (
          <Card.Media aspectRatio="auto" className={twMerge(isRowLayout && "md:order-2 md:flex-[2] md:min-w-0")}>
            <Skeleton width="100%" height={mapHeight} />
          </Card.Media>
        )}
        <Card.Content
          className={twMerge(
            "space-y-2 px-4 md:px-6",
            isRowLayout && "md:order-1 md:flex-[1] md:flex-shrink-0 md:flex md:flex-col md:justify-center"
          )}
        >
          <Skeleton width="100%" height={16} />
          <Skeleton width="80%" height={16} />
        </Card.Content>
      </Card>
    );
  }

  const isRowLayout = layout === "row";

  return (
    <Card
      variant={variant}
      className={twMerge(
        "group/card relative",
        href && "cursor-pointer",
        isRowLayout ? "flex flex-col md:flex-row" : "flex flex-col",
        className
      )}
    >
      {href && (
        <LinkWrapper className="after:absolute after:inset-0 after:z-10">
          <span className="sr-only">{streetAddress}</span>
        </LinkWrapper>
      )}
      {canShowMap && (
        <Card.Media aspectRatio="auto" className={twMerge(isRowLayout && "md:order-2 md:flex-[2] md:min-w-0")}>
          <StaticMap
            height={mapHeight}
            initialViewState={{
              longitude,
              latitude,
              zoom: 14,
            }}
            showMarker
            mapboxAccessToken={mapboxAccessToken}
            showExpandToggle={showMapExpandToggle}
            expandedMapTitle={expandedMapTitle}
            expandedMapAddressLabel={fullAddress}
          />
        </Card.Media>
      )}
      <Card.Content
        className={twMerge(
          "px-4 md:px-6",
          isRowLayout && "md:order-1 md:flex-[1] md:flex-shrink-0 md:flex md:flex-col md:justify-center",
          contentClassName
        )}
      >
        <div className="flex flex-col gap-1 min-w-0 text-left">
          <span
            className="text-xl font-semibold leading-tight text-text-primary group-hover/card:text-text-brand transition-colors"
            data-pii-type={piiEntity ? "address" : undefined}
            data-pii-entity={piiEntity}
          >
            {streetAddress}
          </span>
          {locationLine && (
            <span
              className="text-lg font-normal leading-relaxed text-text-secondary"
              data-pii-type={piiEntity ? "city-state-zip" : undefined}
              data-pii-entity={piiEntity}
            >
              {locationLine}
            </span>
          )}
        </div>
      </Card.Content>
    </Card>
  );
}
